import { useCallback } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { type ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { prefetchRecommendedSpaces } from '@atlassian/rovo-spaces/services/useRecommendedSpaces';

const ROVO_AGENTS_FE_TEAM_NAME = 'rovo-agents-fe';
const HIPAA_ERROR_CODE = 'convoai.rest.browsing.product.has.hipaa.enabled';

const isHipaaError = (error: ApiError): boolean =>
	error.errorAttributes?.errorCode === HIPAA_ERROR_CODE;

export function usePrefetchRecommendedSpacesOnOpenIntent({
	siteId,
}: {
	siteId?: string;
}): () => void {
	const experienceTracker = useAIMateExperienceTracker();

	return useCallback(() => {
		if (!siteId || !fg('rovo_space_recommended')) {
			return;
		}

		prefetchRecommendedSpaces({
			cloudId: siteId,
			product: 'rovo',
			experienceId: 'ai-mate',
			onStart: () => {
				experienceTracker.start({
					id: ExperienceName.PREFETCH_RECOMMENDED_SPACES,
					name: ExperienceName.PREFETCH_RECOMMENDED_SPACES,
				});
			},
			onSuccess: () => {
				experienceTracker.succeed({
					name: ExperienceName.PREFETCH_RECOMMENDED_SPACES,
				});
			},
			onError: (error) => {
				if (isHipaaError(error)) {
					experienceTracker.succeed({
						name: ExperienceName.PREFETCH_RECOMMENDED_SPACES,
					});
					return;
				}

				experienceTracker.fail({
					name: ExperienceName.PREFETCH_RECOMMENDED_SPACES,
					error,
					teamName: ROVO_AGENTS_FE_TEAM_NAME,
				});
			},
		});
	}, [experienceTracker, siteId]);
}
